import {inject} from '@angular/core';
import {CanActivateFn, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {of} from 'rxjs';
import {first, map, switchMap} from 'rxjs/operators';
import {NotificationHelper} from '../root-store/notification/notification-helper';
import {notificationCreate} from '../root-store/notification/notification.actions';
import {selectActivePartnerId, selectRoleAssignations} from './auth-store/auth.selectors';

/**
 * This guard checks if a ZKK is selected (activePartnerId in store) and if not, it redirects to the ZKK selection and shows a notification.
 *
 * Note: **This guard should regularly come _after_ `AuthGuard` and _before_ `HasRightGuard`!**
 * (otherwise the user would get a redirect to the "no rights" view instead of the "please select zkk" notification, which is confusing, as the user only can have rights for a specific zkk)
 * @param _route
 * @param _state
 */
export const zkkSelectedGuard: CanActivateFn = (_route, _state) => {
  const router = inject(Router); // do not inline (the store is async and hence the injection context is not present in the `map`!)
  const store = inject(Store);
  return store.pipe(
    select(selectActivePartnerId),
    switchMap(zkk => {
      if (zkk) {
        return of(null);
      } else {
        return store.pipe(select(selectRoleAssignations));
      }
    }),
    map(roleAssignations => {
      if (roleAssignations) {
        store.dispatch(
          notificationCreate({
            notification: NotificationHelper.createCDNotification({
              type: 'warning',
              title: $localize`:@@NO_ZKK_SELECTION_TITLE:Keine Kundennummer ausgewählt`,
              message: $localize`:@@NO_ZKK_SELECTION_MESSAGE:Bitte wählen Sie eine Kundennummer aus, um fortzufahren.`,
              showInNotificationCenter: false
            })
          })
        );
        // the (legacy) ciso user are not relevant here, as they always have an activePartnerId (which is the user id of a ciso user)
        const isCustomer = roleAssignations.rollenzuweisungenKunde?.length;
        return router.parseUrl(isCustomer ? '/auth/personalUserZkkSelection' : '/auth/zkkSelection');
      } else {
        return true; // zkk present
      }
    }),
    first()
  );
};
